import { useState, useMemo } from 'react';
import { useForumTopics, useWagerFeed } from '../hooks/use-api';
import type { FeedItem, ForumTopic } from '../api/types';
import { Spinner } from '../components/shared/Spinner';
import { EmptyState } from '../components/shared/EmptyState';
import { LoadMoreButton } from '../components/shared/LoadMoreButton';
import { WagerFeedCard } from '../components/feed/WagerFeedCard';
import { TopicFeedCard } from '../components/feed/TopicFeedCard';
import styles from './ForumPage.module.css';

const TYPES: ReadonlyArray<{ readonly value: string; readonly label: string }> = [
  { value: '', label: 'All' },
  { value: 'wager', label: 'Wagers' },
  { value: 'belief', label: 'Beliefs' },
  { value: 'discussion', label: 'Discussion' },
];

type MixedItem =
  | { readonly kind: 'topic'; readonly topic: ForumTopic; readonly sortTime: number }
  | { readonly kind: 'wager'; readonly wager: FeedItem; readonly sortTime: number };

const ForumPage = (): React.ReactElement => {
  const [type, setType] = useState('');

  // Only fetch topics for non-wager filters
  const topicFilter = type === 'wager' ? '__skip__' : type;
  const topics = useForumTopics(topicFilter === '__skip__' ? '' : topicFilter);

  // Only fetch wagers for All or Wagers filter
  const wagers = useWagerFeed();
  const showWagers = type === '' || type === 'wager';
  const showTopics = type !== 'wager';

  // Merge and sort by timestamp descending
  const mixedItems = useMemo((): ReadonlyArray<MixedItem> => {
    const items: MixedItem[] = [];

    if (showTopics) {
      for (const topic of topics.items) {
        items.push({
          kind: 'topic',
          topic,
          sortTime: new Date(topic.lastActivityAt).getTime(),
        });
      }
    }

    if (showWagers) {
      for (const wager of wagers.items) {
        items.push({
          kind: 'wager',
          wager,
          sortTime: new Date(wager.createdAt).getTime(),
        });
      }
    }

    return items.sort((a, b) => b.sortTime - a.sortTime);
  }, [topics.items, wagers.items, showTopics, showWagers]);

  const isLoading = (showTopics && topics.isLoading) || (showWagers && wagers.isLoading);
  const error = topics.error ?? wagers.error;
  const hasMore = (showTopics && topics.hasMore) || (showWagers && wagers.hasMore);
  const isFetchingNext = topics.isFetchingNextPage || wagers.isFetchingNextPage;

  const handleLoadMore = (): void => {
    if (showTopics && topics.hasMore) topics.loadMore();
    if (showWagers && wagers.hasMore) wagers.loadMore();
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Forum</h1>

      <div className={styles.filters} role="tablist">
        {TYPES.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={type === t.value}
            className={`${styles.filterPill} ${type === t.value ? styles.filterPillActive : ''}`}
            onClick={() => setType(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className={styles.loadingState}>
          <Spinner />
        </div>
      )}

      {error !== null && (
        <div className={styles.errorState}>
          <p className={styles.errorText}>
            Failed to load forum{error instanceof Error ? `: ${error.message}` : ''}
          </p>
        </div>
      )}

      {!isLoading && mixedItems.length === 0 && error === null && (
        <EmptyState message="No activity yet." />
      )}

      {mixedItems.length > 0 && (
        <div className={styles.topicList}>
          {mixedItems.map((item) => {
            if (item.kind === 'wager' && item.wager.wagerId) {
              const w = item.wager;
              return (
                <WagerFeedCard
                  key={`wager-${w.wagerId}`}
                  wagerId={w.wagerId ?? ''}
                  status={w.status ?? 'open'}
                  statement={w.statement ?? ''}
                  category={w.category ?? ''}
                  creator={w.creator?.entityName ?? ''}
                  creatorId={w.creator?.entityId ?? ''}
                  opponent={w.opponent?.entityName ?? null}
                  opponentId={w.opponent?.entityId ?? null}
                  stake={w.creator?.stake ?? 0}
                  opponentStake={w.opponent?.stake ?? null}
                  timestamp={w.createdAt}
                  resolution={w.resolution ? { winnerName: w.resolution.winnerName ?? '', payout: w.resolution.payout ?? 0, summary: w.resolution.summary ?? '' } : null}
                />
              );
            }
            if (item.kind === 'topic') {
              const topic = item.topic;
              return (
                <TopicFeedCard
                  key={topic.topicId}
                  topicId={topic.topicId}
                  title={topic.title}
                  type={topic.type}
                  creatorId={topic.creatorId}
                  creatorName={topic.creatorName}
                  postCount={topic.postCount}
                  lastActivityAt={topic.lastActivityAt}
                  createdAt={topic.createdAt}
                />
              );
            }
            return null;
          })}
          <LoadMoreButton onClick={handleLoadMore} isLoading={isFetchingNext} hasMore={hasMore} />
        </div>
      )}
    </div>
  );
};

export { ForumPage };
